import { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  Alert,
  ScrollView,
} from "react-native";
import { Link } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { Image } from "expo-image";

/**
 * Login Screen Component
 */
export default function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, isLoading } = useAuth();
  const colors = useThemeColors();

  /**
   * Handle login form submission
   */
  const handleLogin = async () => {
    if (!email || !password) {
      Alert.alert("Fout", "Vul alle velden in");
      return;
    }

    try {
      await signIn(email, password);
    } catch (error: any) {
      Alert.alert("Fout", error.message || "Er ging iets mis bij het inloggen");
    }
  };

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
      >
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ flexGrow: 1, justifyContent: "center", paddingHorizontal: 24 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Logo */}
          <View className="items-center mb-12">
            <View className="w-28 h-28 rounded-3xl overflow-hidden mb-4 shadow-lg">
              <Image
                source={require("@/assets/app_icon/ios-light.png")}
                style={{ width: "100%", height: "100%" }}
                contentFit="cover"
              />
            </View>
            <Text className="text-3xl font-bold" style={{ color: colors.text }}>
              Staatsen App
            </Text>
            <Text className="mt-1" style={{ color: colors.textSecondary }}>
              Onze familie app
            </Text>
          </View>

          {/* Form */}
          <View
            className="rounded-2xl p-6"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <Text className="text-2xl font-bold mb-6" style={{ color: colors.text }}>
              Welkom terug!
            </Text>

            {/* Email Input */}
            <View className="mb-4">
              <Text className="mb-2 font-medium" style={{ color: colors.text }}>
                Email
              </Text>
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{ backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="mail-outline" size={20} color={colors.textTertiary} />
                <TextInput
                  className="flex-1 py-3 px-3"
                  style={{ color: colors.text }}
                  placeholder="Login met jouw email"
                  placeholderTextColor={colors.textTertiary}
                  value={email}
                  onChangeText={setEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  autoComplete="email"
                />
              </View>
            </View>

            {/* Password Input */}
            <View className="mb-6">
              <Text className="mb-2 font-medium" style={{ color: colors.text }}>
                Wachtwoord
              </Text>
              <View
                className="flex-row items-center rounded-xl px-4"
                style={{ backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="lock-closed-outline" size={20} color={colors.textTertiary} />
                <TextInput
                  className="flex-1 py-3 px-3"
                  style={{ color: colors.text }}
                  placeholder="Jouw wachtwoord"
                  placeholderTextColor={colors.textTertiary}
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  autoComplete="password"
                />
                <TouchableOpacity
                  onPress={() => setShowPassword(!showPassword)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  accessibilityRole="button"
                  accessibilityLabel={showPassword ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                >
                  <Ionicons
                    name={showPassword ? "eye-outline" : "eye-off-outline"}
                    size={20}
                    color={colors.textTertiary}
                  />
                </TouchableOpacity>
              </View>
            </View>

            {/* Login Button */}
            <TouchableOpacity
              className="rounded-xl py-4 items-center"
              style={{
                backgroundColor: isLoading ? `${colors.primary}80` : colors.primary,
              }}
              onPress={handleLogin}
              disabled={isLoading}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Inloggen"
            >
              <Text className="text-white font-semibold text-lg">
                {isLoading ? "Laden..." : "Log In"}
              </Text>
            </TouchableOpacity>

            {/* Sign Up Link */}
            <View className="flex-row justify-center mt-6">
              <Text style={{ color: colors.textSecondary }}>Nog geen account? </Text>
              <Link href="/(auth)/signup" asChild>
                <TouchableOpacity accessibilityRole="link">
                  <Text className="font-semibold" style={{ color: colors.primary }}>
                    Sign Up
                  </Text>
                </TouchableOpacity>
              </Link>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
